import type { FormField } from '@/types/api/form'
import { FormFieldType } from '@/types/enums'

import { isDataField } from './fieldAccess'
import { fieldChoices } from './recordFilters'

/** Never prefilled: a link cannot carry a file or pick a mailbox message. */
const NOT_PREFILLED: string[] = [FormFieldType.FILE_UPLOAD, FormFieldType.EMAIL_MESSAGE]

/**
 * Answers taken from a link's query string, by field key: `?utm_source=facebook&city=Pune`.
 * A value the field would not accept (per `check`) is ignored rather than blocking the form.
 */
export function prefillValues(
  fields: readonly FormField[],
  params: Record<string, string>,
  check: (value: string, field: FormField) => string | null
): Record<string, string> {
  const values: Record<string, string> = {}
  for (const field of fields) {
    const raw = params[field.key]
    if (raw === undefined || !isDataField(field) || NOT_PREFILLED.includes(field.type)) continue
    if (field.type === FormFieldType.SELECT && !fieldChoices(field).includes(raw)) continue
    if (check(raw, field) === null) values[field.id] = raw
  }
  return values
}

/** Fields that can start with one of their choices picked (`options.defaultValue`). */
export const DEFAULT_CHOICE_TYPES: string[] = [FormFieldType.SELECT, FormFieldType.RADIO]

/** The choice each SELECT / RADIO field starts with on a new record, by field id. */
export function defaultValues(fields: readonly FormField[]): Record<string, string> {
  const values: Record<string, string> = {}
  for (const field of fields) {
    const value = (field.options as Record<string, unknown> | null)?.defaultValue
    if (typeof value === 'string' && DEFAULT_CHOICE_TYPES.includes(field.type) && fieldChoices(field).includes(value)) {
      values[field.id] = value
    }
  }
  return values
}

/** Whether the public form leaves this field out (it can still be filled from the link). */
export function isHiddenOnPublicForm(field: Pick<FormField, 'options'>): boolean {
  return (field.options as Record<string, unknown> | null)?.hideOnPublicForm === true
}
